/**
 * Tokens de movimiento.
 *
 * Todas las animaciones de la aplicacion salen de aqui. Si cada componente
 * eligiera su propia duracion y su propia curva, la interfaz se sentiria hecha
 * por cinco personas distintas: es el mismo motivo por el que los colores estan
 * centralizados en `theme.css` y no sueltos en cada archivo.
 *
 * Los valores siguen las guias de movimiento de Material y de Apple:
 *
 *   - Micro-interacciones entre 150 y 300 ms. Arriba de 500 ms se siente lento.
 *   - Al ENTRAR, ease-out: arranca rapido y frena. Al SALIR, ease-in.
 *   - Lo que sale dura un 60-70 % de lo que entra: irse debe sentirse agil.
 *   - Listas escalonadas de 30 a 50 ms por elemento.
 *
 * El movimiento aqui siempre significa algo —de donde viene un elemento, que
 * cifra cambio, que algo llego— nunca es decoracion.
 */

/** Duraciones en segundos, que es lo que espera framer-motion. */
export const DURATION = {
  /** Cambios de estado inmediatos: hover, foco, presion. */
  instant: 0.12,
  /** Micro-interacciones: entrada de un campo, un aviso. */
  quick: 0.22,
  /** Entradas con recorrido: una tarjeta, una hoja modal. */
  normal: 0.32,
  /** Secuencias compuestas. Nunca mas que esto. */
  slow: 0.45,
} as const;

/** Salir dura menos que entrar. */
export const EXIT_FACTOR = 0.65;

/**
 * Curvas. Las de entrada frenan al final; las de salida arrancan lento.
 * Son las mismas que `--ease-out-soft` de `theme.css`, para que el movimiento
 * hecho en CSS y el hecho en JavaScript no se contradigan.
 */
export const EASE = {
  out: [0.22, 1, 0.36, 1],
  in: [0.64, 0, 0.78, 0],
  inOut: [0.65, 0, 0.35, 1],
} as const;

/** Separacion entre elementos de una lista que entra. */
export const STAGGER = 0.045;

/**
 * Resorte para lo que responde al dedo: botones y controles.
 * Un resorte se siente fisico donde una curva fija se siente mecanica.
 */
export const SPRING = { type: 'spring', stiffness: 420, damping: 32, mass: 0.7 } as const;

/** Entrada estandar: sube un poco y aparece. */
export const RISE = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
} as const;

/** Contenedor que escalona a sus hijos. */
export const STAGGER_PARENT = {
  hidden: {},
  visible: { transition: { staggerChildren: STAGGER, delayChildren: 0.06 } },
} as const;

/**
 * True si el sistema pide menos movimiento.
 *
 * La regla global de CSS apaga las animaciones declaradas en hojas de estilo,
 * pero no alcanza a lo que se anima desde JavaScript, asi que hay que
 * preguntarlo a mano antes de arrancar una linea de tiempo.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
